import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyThalesMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Thales cyber-defense gradient - deep blue to red accents
  const bgGradient = (
    <defs>
      <linearGradient id="thalesGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#001F3F", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#003D7A", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#8B0000", stopOpacity: 0.3 }} />
      </linearGradient>
      <radialGradient id="radarGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" style={{ stopColor: "#00FF00", stopOpacity: 0.4 }} />
        <stop offset="100%" style={{ stopColor: "#00FF00", stopOpacity: 0 }} />
      </radialGradient>
    </defs>
  );

  // Security layers - concentric protection rings
  const securityLayers: JSX.Element[] = [];
  for (let layer = 0; layer < 16; layer++) {
    const radius = 50 + layer * 25;
    const segments = 8 + layer * 2;
    
    for (let s = 0; s < segments; s++) {
      const angle1 = (s / segments) * 2 * Math.PI;
      const angle2 = ((s + 0.7) / segments) * 2 * Math.PI;
      
      const x1 = Math.cos(angle1) * radius;
      const y1 = Math.sin(angle1) * radius;
      const x2 = Math.cos(angle2) * radius;
      const y2 = Math.sin(angle2) * radius;
      
      securityLayers.push(
        <line
          key={`layer-${layer}-${s}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={layer % 2 === 0 ? "#00BFFF" : "#FF4500"}
          strokeWidth={2}
          opacity={0.3 - layer * 0.012}
        />
      );
    }
  }

  // Radar grids - scanning pattern with sweep lines
  const radarGrids: JSX.Element[] = [];
  const radarRings = 12;
  for (let r = 1; r <= radarRings; r++) {
    const radius = r * 33;
    radarGrids.push(
      <circle
        key={`radar-${r}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#00FF00"
        strokeWidth={r % 3 === 0 ? 2 : 1}
        opacity={0.25 - r * 0.012}
      />
    );
  }

  // Radar sweep lines
  const radarSweeps: JSX.Element[] = [];
  for (let sweep = 0; sweep < 8; sweep++) {
    const angle = (sweep / 8) * 2 * Math.PI;
    const x = Math.cos(angle) * 390;
    const y = Math.sin(angle) * 390;
    
    radarSweeps.push(
      <line
        key={`sweep-${sweep}`}
        x1={0}
        y1={0}
        x2={x}
        y2={y}
        stroke="#00FF00"
        strokeWidth={1.5}
        opacity={0.2}
        strokeDasharray="10,5"
      />
    );
  }

  // Infrared gradients - heat signature waves
  const infraredWaves: JSX.Element[] = [];
  for (let wave = 0; wave < 14; wave++) {
    const points: string[] = [];
    const y = -480 + wave * 70;
    
    for (let p = 0; p <= 40; p++) {
      const t = p / 40;
      const x = -360 + t * 720;
      const yOffset = Math.sin(t * Math.PI * 3 + wave * 0.5) * 25;
      points.push(`${x},${y + yOffset}`);
    }
    
    infraredWaves.push(
      <polyline
        key={`infrared-${wave}`}
        points={points.join(" ")}
        fill="none"
        stroke="#FF4500"
        strokeWidth={1.5}
        opacity={0.15}
      />
    );
  }

  // Encrypted geometry nodes - hexagonal trust points
  const encryptedNodes: JSX.Element[] = [];
  const nodeRings = [140, 220, 300];
  
  nodeRings.forEach((nodeR, ri) => {
    const nodeCount = 6 + ri * 2;
    for (let n = 0; n < nodeCount; n++) {
      const angle = (n / nodeCount) * 2 * Math.PI;
      const nx = Math.cos(angle) * nodeR;
      const ny = Math.sin(angle) * nodeR;
      
      // Hexagonal encryption symbol
      const hexPoints: string[] = [];
      for (let h = 0; h < 6; h++) {
        const ha = (h / 6) * 2 * Math.PI;
        const hexSize = 20 - ri * 3;
        const hx = nx + Math.cos(ha) * hexSize;
        const hy = ny + Math.sin(ha) * hexSize;
        hexPoints.push(`${hx},${hy}`);
      }
      
      encryptedNodes.push(
        <g key={`node-${ri}-${n}`}>
          <polygon
            points={hexPoints.join(" ")}
            fill="none"
            stroke="#00BFFF"
            strokeWidth={2}
            opacity={0.5}
          />
          <circle
            cx={nx}
            cy={ny}
            r={8}
            fill="#00FF00"
            opacity={0.3}
          />
        </g>
      );
    }
  });

  // Cyber-defense perimeter - outer protection boundary
  const perimeterLines: JSX.Element[] = [];
  for (let p = 0; p < 32; p++) {
    const angle = (p / 32) * 2 * Math.PI;
    const innerR = 360;
    const outerR = 390;
    const x1 = Math.cos(angle) * innerR;
    const y1 = Math.sin(angle) * innerR;
    const x2 = Math.cos(angle) * outerR;
    const y2 = Math.sin(angle) * outerR;
    
    perimeterLines.push(
      <line
        key={`perimeter-${p}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#FF4500"
        strokeWidth={p % 4 === 0 ? 3 : 1.5}
        opacity={p % 4 === 0 ? 0.5 : 0.25}
      />
    );
  }

  // Pattern recognition grid - AI detection overlay
  const patternGrid: JSX.Element[] = [];
  const gridSize = 18;
  const cellSize = 40;
  
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      const x = -360 + i * cellSize;
      const y = -360 + j * cellSize;
      const detected = Math.random() > 0.7;
      
      if (detected) {
        patternGrid.push(
          <rect
            key={`pattern-${i}-${j}`}
            x={x}
            y={y}
            width={cellSize}
            height={cellSize}
            fill="none"
            stroke="#00BFFF"
            strokeWidth={1}
            opacity={0.15}
          />
        );
      }
    }
  }

  // Central radar core with glow
  const radarCore = (
    <g>
      <circle cx={0} cy={0} r={80} fill="url(#radarGlow)" />
      <circle cx={0} cy={0} r={45} fill="none" stroke="#00FF00" strokeWidth={3} opacity={0.6} />
      <circle cx={0} cy={0} r={30} fill="none" stroke="#00BFFF" strokeWidth={2.5} opacity={0.5} />
      <circle cx={0} cy={0} r={15} fill="#00FF00" opacity={0.4} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#thalesGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {patternGrid}
        {infraredWaves}
        {securityLayers}
        {radarGrids}
        {radarSweeps}
        {perimeterLines}
        {encryptedNodes}
        {radarCore}
      </g>
    </svg>
  );
};
